import React, { useCallback, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Form } from '@opendoor/atoms/form';
import { Text } from '@opendoor/atoms/text';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';

import { useAnalyticsPrefix, useAuthActions } from '../../AuthContext';
import { GENERIC_AUTH_FAILURE_MESSAGE } from '../../errors';
import type { AuthError } from '../../sessionClient';
import { AuthFormError } from './primitives/AuthFormError';
import { CheckEmailNotice } from './primitives/CheckEmailNotice';
import { readEmailFromQueryParam } from './queryPrefill';

const ANALYTICS_COMPONENT = 'reset-password-request';

const UNKNOWN_EMAIL_ERROR = 'We don’t recognize that email. Try again with another one.';

const emailSchema = z.object({ email: z.email() });

export interface ResetPasswordRequestModuleProps {
  /**
   * Heading above the form. A thin naming slot — defaults to the Figma copy;
   * pass `null` to render no heading.
   */
  heading?: React.ReactNode;
  /** Subheading below the heading. Defaults to the Figma copy. */
  subheading?: React.ReactNode;
  /**
   * Seeds the email field, taking precedence over the internal `?email=`
   * self-read. An SSR consumer must pass this: the self-read is empty on the
   * server (no `window`) and populated on the client, which hydration-mismatches
   * a server-rendered page. Supplying the value the server already knows makes
   * both renders agree. Omit it and the self-read still covers client-only
   * mounts.
   */
  defaultEmail?: string;
  /**
   * Called when a reset request fails on the network/server (not for the benign
   * new-account outcome). An additive observability seam — the module still
   * surfaces its own inline error regardless of whether this is provided.
   */
  onError?: (error: AuthError) => void;
}

const DEFAULT_HEADING = 'Reset your password';
const DEFAULT_SUBHEADING = 'Enter your email and we’ll send you a link to reset your password.';

/**
 * The V2 reset-request surface: an email field that drives the human-status-
 * guarded `requestPasswordReset` write-path. An existing account transitions to
 * the check-email surface; a new/unknown email (no reset email is sent) and any
 * failure surface in the form-level error region rather than claiming a link
 * was sent.
 *
 * Self-contained — reads the write-path from `useAuthActions()` and holds its own
 * email/step state, so it needs no `AccountFormProvider`. It works standalone
 * on the reset-password page today and is the surface the AccountForm shell
 * mounts for the `'reset-password'` step (the entry point PasswordModule's
 * "Forgot your password?" / locked-account CTA routes to via
 * `setStep('reset-password')`; shell routing lands in GROWTH-2686).
 */
export const ResetPasswordRequestModule: React.FC<ResetPasswordRequestModuleProps> = ({
  heading = DEFAULT_HEADING,
  subheading = DEFAULT_SUBHEADING,
  defaultEmail,
  onError,
}) => {
  const { requestPasswordReset } = useAuthActions();
  const analyticsPrefix = useAnalyticsPrefix();
  const analyticsName = (action: string): string =>
    `${analyticsPrefix}-${ANALYTICS_COMPONENT}-${action}`;

  const [serverError, setServerError] = useState<string | null>(null);
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);
  const [isSetup, setIsSetup] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const form = useForm({
    // `defaultEmail` wins over the self-read so a server-rendered consumer can
    // pass the value the server already knows and avoid a hydration mismatch;
    // the self-read remains the fallback for client-only mounts.
    defaultValues: { email: defaultEmail ?? readEmailFromQueryParam() },
    validators: { onChange: emailSchema },
    // Clear a stale server error (e.g. "we don't recognize that email") as soon
    // as the field is edited; onSubmit doesn't run until validation passes, so
    // it can't clear it on its own before an invalid-then-corrected retype.
    listeners: { onChange: () => setServerError(null) },
    onSubmit: async ({ value }) => {
      setServerError(null);
      const result = await requestPasswordReset(value.email);
      if (!result.success) {
        // Route the failure into local state rather than throwing — a throw here
        // would escape TanStack's handleSubmit as an unhandled rejection.
        setServerError(GENERIC_AUTH_FAILURE_MESSAGE);
        onError?.(result.error);
        return;
      }
      if (result.data.isNewHuman) {
        // No reset email is sent for an account that doesn't exist; don't claim
        // one was.
        setServerError(UNKNOWN_EMAIL_ERROR);
        return;
      }
      // A generated-password (passwordless) account has no password to reset —
      // Rails mails a set-up link, not a reset link — so the confirmation copy
      // must say so.
      setIsSetup(result.data.hasGeneratedPassword);
      setSentToEmail(value.email);
    },
  });

  // The check-email surface's resend. Returns whether a reset email actually
  // went out, so CheckEmailNotice restarts its cooldown only on a real send;
  // a failure is surfaced in the notice's error region rather than swallowed.
  const handleResend = useCallback(async (): Promise<boolean> => {
    if (sentToEmail == null) {
      return false;
    }
    setResendError(null);
    const result = await requestPasswordReset(sentToEmail);
    if (result.success && !result.data.isNewHuman) {
      return true;
    }
    setResendError(GENERIC_AUTH_FAILURE_MESSAGE);
    return false;
  }, [requestPasswordReset, sentToEmail]);

  if (sentToEmail != null) {
    // The request already sent the email, so start the notice in its cooldown.
    return (
      <CheckEmailNotice
        heading="Check your email"
        description={
          isSetup
            ? `We sent an email to ${sentToEmail}. It includes a link to set up your account.`
            : `We sent an email to ${sentToEmail}. It includes a link to reset your password.`
        }
        error={resendError}
        onMount="cooldown"
        onResend={handleResend}
        analyticsName={analyticsName('resend')}
      />
    );
  }

  return (
    <Box className="flex w-full flex-col gap-4">
      {heading != null ? (
        <Text as="h1" className="text-subheader-md text-od-content-primary">
          {heading}
        </Text>
      ) : null}
      {subheading != null ? (
        <Text as="p" className="text-body-sm text-od-content-secondary">
          {subheading}
        </Text>
      ) : null}
      {/* Server-only error region (rendered outside Form.Root so it surfaces the
          unknown-email / failure messages without also echoing the inline email
          field error). */}
      <AuthFormError serverErrors={serverError} />
      <Form.Root
        form={form}
        noValidate
        scrollToFirstError={false}
        className="flex w-full flex-col gap-4"
      >
        <Form.Input
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="Enter your email here"
          analyticsName={analyticsName('email')}
        />
        <Form.SubmitButton
          className="w-full"
          aria-label="Send reset link"
          analyticsName={analyticsName('submit')}
        >
          Send reset link
        </Form.SubmitButton>
      </Form.Root>
    </Box>
  );
};
